import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import axios from "axios";

import Header from "../../components/Header";
import { AuthContext } from "../../components/AuthContext";
import config from "../../config";
import { HPC_COLORS } from "../hpc/hpcTheme";
import { authConfig } from "./shared/paymentUtils";
import TebaPaymentPanel from "./TebaPaymentPanel";
import { StandingChip } from "./tebaPaymentUi";

// Desktop columns fill the space below the page header and scroll on their own (same as HPU).
const DESKTOP_COLUMN_HEIGHT = "calc(100dvh - 210px)";
const SEARCH_DELAY_MS = 300;

const TebaPaymentReceive = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { role } = useContext(AuthContext);
  const isGlobalAdmin = role === "global_admin";

  const [members, setMembers] = useState([]);
  const [more, setMore] = useState(false);
  const [search, setSearch] = useState("");
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [selectedMembershipId, setSelectedMembershipId] = useState(
    location.state?.membershipId || location.state?.member?.membership_id || searchParams.get("member") || null,
  );
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const workspaceRef = useRef(null);
  const listRequestRef = useRef(0);
  const selectRequestRef = useRef(0);

  const loadMembers = useCallback(async (query, branch) => {
    const requestId = listRequestRef.current + 1;
    listRequestRef.current = requestId;
    setListLoading(true);
    setListError("");
    try {
      const response = await axios.get(`${config.apiBaseUrl}/payment-receive/teba/members`, {
        ...authConfig(),
        params: { q: query || undefined, branch_id: branch || undefined },
      });
      if (listRequestRef.current !== requestId) return;
      setMembers(response.data.members || []);
      setMore(Boolean(response.data.more));
    } catch (error) {
      if (listRequestRef.current === requestId) {
        setListError(error.response?.data?.error || "Could not load TEBA members.");
      }
    } finally {
      if (listRequestRef.current === requestId) setListLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => loadMembers(search.trim(), branchId), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [search, branchId, loadMembers]);

  useEffect(() => {
    if (!isGlobalAdmin) return;
    axios.get(`${config.apiBaseUrl}/get_branches`, authConfig())
      .then((response) => setBranches(response.data.branches || response.data || []))
      .catch(() => setBranches([]));
  }, [isGlobalAdmin]);

  const loadSummary = useCallback(async (membershipId, { quiet = false } = {}) => {
    const requestId = selectRequestRef.current + 1;
    selectRequestRef.current = requestId;
    if (!quiet) {
      setSummary(null);
      setSummaryLoading(true);
    }
    setSummaryError("");
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/payment-receive/teba/members/${encodeURIComponent(membershipId)}/summary`,
        authConfig(),
      );
      if (selectRequestRef.current === requestId) setSummary(response.data);
    } catch (error) {
      if (selectRequestRef.current === requestId) {
        setSummaryError(error.response?.data?.error || "Could not load TEBA member information.");
      }
    } finally {
      if (selectRequestRef.current === requestId) setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedMembershipId) loadSummary(selectedMembershipId);
    // Only on first load: a member chosen from the member list or profile.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectMember = (member) => {
    if (selectedMembershipId === member.membership_id && summary) return;
    setSelectedMembershipId(member.membership_id);
    // Only the workspace column moves back to its top; the page itself never scrolls.
    if (workspaceRef.current) workspaceRef.current.scrollTop = 0;
    loadSummary(member.membership_id);
  };

  const refreshAfterPayment = async () => {
    if (selectedMembershipId) await loadSummary(selectedMembershipId, { quiet: true });
    const workspace = workspaceRef.current;
    if (workspace) {
      if (isDesktop) workspace.scrollTo({ top: 0, behavior: "smooth" });
      else workspace.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    loadMembers(search.trim(), branchId);
  };

  const clearSelection = () => {
    selectRequestRef.current += 1;
    setSelectedMembershipId(null);
    setSummary(null);
    setSummaryError("");
    setSummaryLoading(false);
  };

  const searchPanel = (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        height: isDesktop ? DESKTOP_COLUMN_HEIGHT : "auto",
        minHeight: isDesktop ? 420 : 0,
      }}
    >
      <CardContent sx={{ pb: 1, flexShrink: 0 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography variant="h5" fontWeight={700}>Find member</Typography>
          <Tooltip title="Refresh list">
            <span>
              <IconButton color="inherit" onClick={() => loadMembers(search.trim(), branchId)} disabled={listLoading} aria-label="Refresh member list">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name, membership ID, NIC or phone"
            sx={{ flex: "1 1 220px", minWidth: 0 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
          {isGlobalAdmin && (
          <FormControl size="small" sx={{ flex: "1 1 150px", minWidth: 140 }}>
            <InputLabel id="teba-payment-branch-label">Branch</InputLabel>
            <Select
              labelId="teba-payment-branch-label"
              label="Branch"
              value={branchId}
              onChange={(event) => setBranchId(event.target.value)}
            >
              <MenuItem value="">All branches</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.branch_id} value={String(branch.branch_id)}>{branch.branch_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          )}
        </Box>
        {!listLoading && !listError && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
            {more ? `First ${members.length} members – refine the search to see more` : `${members.length} members`}
          </Typography>
        )}
      </CardContent>

      {listError && (
        <Alert severity="error" sx={{ mx: 2, mb: 1 }} action={<Button color="inherit" onClick={() => loadMembers(search.trim(), branchId)}>Retry</Button>}>
          {listError}
        </Alert>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 1,
          pb: 1,
          ...(isDesktop ? {} : { maxHeight: "60vh" }),
        }}
      >
        {listLoading && members.length === 0 ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : members.length === 0 ? (
          <Alert severity="info" sx={{ mx: 1 }}>
            {search ? "No TEBA members match this search." : "No TEBA members are available."}
          </Alert>
        ) : (
          <List dense disablePadding>
            {members.map((member, index) => {
              const selected = selectedMembershipId === member.membership_id;
              const details = [member.membership_id, member.subscription_name || "No subscription", member.branch_name]
                .filter(Boolean)
                .join(" · ");
              return (
                <ListItemButton
                  key={member.member_id || member.membership_id}
                  selected={selected}
                  onClick={() => selectMember(member)}
                  divider={index < members.length - 1}
                  sx={{
                    py: 1,
                    px: 1.25,
                    gap: 1,
                    alignItems: "center",
                    borderLeft: "3px solid transparent",
                    "&:hover": { backgroundColor: "action.hover" },
                    "&.Mui-selected": {
                      borderLeftColor: HPC_COLORS.hpuOrange,
                      backgroundColor: alpha(HPC_COLORS.hpuOrange, 0.14),
                    },
                    "&.Mui-selected:hover": { backgroundColor: alpha(HPC_COLORS.hpuOrange, 0.2) },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: 14 }}>
                      {member.full_name || `Member #${member.membership_id}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      {details}
                    </Typography>
                  </Box>
                  <StandingChip standing={member.standing} paidUntil={member.paid_until} expiryDate={member.expiry_date}
                    sx={{ flexShrink: 0 }} />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Card>
  );

  const workspaceContent = !selectedMembershipId ? (
    <Card sx={{ height: isDesktop ? "100%" : "auto", minHeight: 240 }}>
      <CardContent
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 1.5,
          py: 6,
        }}
      >
        <PaymentsOutlinedIcon sx={{ fontSize: 48, color: HPC_COLORS.hpuOrange }} />
        <Typography variant="h5" fontWeight={700}>No member selected</Typography>
        <Typography variant="body1" color="text.secondary">
          Search and select a member to receive a payment.
        </Typography>
      </CardContent>
    </Card>
  ) : summaryLoading ? (
    <Card><CardContent><Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box></CardContent></Card>
  ) : summaryError ? (
    <Alert severity="error" action={<Button color="inherit" onClick={() => loadSummary(selectedMembershipId)}>Retry</Button>}>{summaryError}</Alert>
  ) : summary ? (
    <TebaPaymentPanel key={summary.member.membership_id} summary={summary} onPaymentCompleted={refreshAfterPayment} />
  ) : null;

  return (
    <Box m={{ xs: "12px", sm: "16px", md: "20px" }}>
      <Header title="Payment Receive" subtitle="TEBA · Receive and review subscription payments" />

      {isDesktop ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(320px, 35%) minmax(0, 1fr)",
            gap: 2,
            alignItems: "start",
          }}
        >
          {searchPanel}
          <Box
            ref={workspaceRef}
            sx={{
              height: DESKTOP_COLUMN_HEIGHT,
              minHeight: 420,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            {workspaceContent}
          </Box>
        </Box>
      ) : selectedMembershipId ? (
        <Box ref={workspaceRef}>
          <Button startIcon={<ArrowBackIcon />} variant="outlined" color="inherit" onClick={clearSelection} sx={{ mb: 2 }}>
            Back to search
          </Button>
          {workspaceContent}
        </Box>
      ) : (
        searchPanel
      )}
    </Box>
  );
};

export default TebaPaymentReceive;
